import { IS_DEV_ENV } from "constants/app.constants";
import {
  BLACK_HEX,
  DEFAULT_LINE_THICKNESS,
  DEFAULT_MIDSTREAM_CHART_TYPE_PARAM_X_PROPERTIES,
  DEFAULT_MIDSTREAM_CROSS_PLOT_CHART_TYPE_PARAM_Y_PROPERTIES,
  DEFAULT_OPACITY
} from "constants/chart.constants";
import { EntityType } from "constants/entities.enum";
import { createEntityState } from "entities/charts/factory";
import { createMergeWithDefaults } from "entities/utils";
import { EvaChartFeatures } from "types";

import { RegressionType } from "models/regressionType";

import {
  DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
  DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
  DEFAULT_LINE_SIZE_FEATURE_ENTITY
} from "./feature";

const MIDSTREAM_CROSS_PLOT_FEATURES: Partial<EvaChartFeatures> = {
  screenshot: createEntityState(EntityType.ScreenshotFeature, {
    show: true,
    enabled: true
  }),
  fullScreen: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  datatable: createEntityState(EntityType.ChartDataTableFeature, {
    show: true,
    enabled: true
  }),
  spaghetti: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: false
  }),
  debug: createEntityState(EntityType.ChartsFeature, {
    show: IS_DEV_ENV,
    enabled: IS_DEV_ENV
  }),
  cutoff: createEntityState(EntityType.CutOffChartFeature, {
    show: true,
    enabled: true
  }),
  shutInMonths: createEntityState(EntityType.ShutInMonthsChartFeature, {
    show: true,
    enabled: true
  }),
  movingAverageDays: createEntityState(EntityType.MovingAverageDaysChartFeature, {
    show: true,
    enabled: true
  }),
  survivorBias: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: true
  }),
  useSeriesStyleOverride: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: false
  }),
  useWeightedRatioAverage: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true,
    active: true
  }),
  lockUnits: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  lasso: createEntityState(EntityType.LassoFeature, {
    show: true,
    enabled: true
  }),
  chartFocus: createEntityState(EntityType.ChartFocusMidStreamFeature, {
    show: true,
    enabled: true
  }),
  legend: createEntityState(EntityType.ChartsFeature, {
    show: true,
    enabled: true
  }),
  pointSize: createEntityState(EntityType.PointSizeFeature, {
    show: true,
    enabled: true
  }),
  pointOpacity: createEntityState(EntityType.PointOpacityFeature, {
    show: true,
    enabled: true
  }),
  lineSize: {
    ...DEFAULT_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  lineOpacity: {
    ...DEFAULT_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineSize: {
    ...DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  highlightedLineOpacity: {
    ...DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY,
    show: false,
    enabled: false
  },
  chartTypeParam: createEntityState(EntityType.ChartTypeParamFeature, {
    properties: {
      x: DEFAULT_MIDSTREAM_CHART_TYPE_PARAM_X_PROPERTIES,
      y: DEFAULT_MIDSTREAM_CROSS_PLOT_CHART_TYPE_PARAM_Y_PROPERTIES
    }
  }),
  logAxis: createEntityState(EntityType.LogAxisFeature, {
    properties: {
      x: { active: false },
      y: { active: false }
    }
  }),
  bestFitToggle: createEntityState(EntityType.BestFitToggleFeature, {
    properties: {
      regressionType: RegressionType.Linear,
      color: BLACK_HEX,
      thickness: DEFAULT_LINE_THICKNESS,
      forceRegressionThroughOrigin: false,
      opacity: DEFAULT_OPACITY
    }
  }),
  histogramToggle: createEntityState(EntityType.HistogramToggleFeature, {}),
  jitterPlotToggle: createEntityState(EntityType.JitterPlotToggleFeature, {}),
  quarterSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  unitSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  halfSlope: createEntityState(EntityType.SlopeLineFeature, {}),
  useProbitLineOfBestFitData: createEntityState(EntityType.ChartsFeature, {})
};

export const crossPlotMidstreamFeatures = createMergeWithDefaults(
  MIDSTREAM_CROSS_PLOT_FEATURES
);
